import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Connects to data-controller="notifications"
export default class extends Controller {
  static targets = ["badge", "dropdown", "list", "toggle"]
  static values = { 
    userId: Number,
    enabled: Boolean 
  }

  connect() {
    if (!this.enabledValue || !this.userIdValue) {
      return
    }

    this.boundHandleDocumentClick = (event) => {
      if (!this.element.contains(event.target)) {
        this.closeDropdown()
      }
    }

    this.createCableSubscription()
    this.setupEventListeners()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    if (this.boundHandleDocumentClick) {
      document.removeEventListener('click', this.boundHandleDocumentClick)
    }
  }

  createCableSubscription() {
    this.consumer = createConsumer()
    
    this.subscription = this.consumer.subscriptions.create(
      { channel: "NotificationsChannel" },
      {
        connected: () => {
          console.log("Connected to notifications channel")
          this.loadRecentNotifications()
        },

        disconnected: () => {
          console.log("Disconnected from notifications channel")
        },

        received: (data) => {
          this.handleNotification(data)
        }
      }
    )
  }

  setupEventListeners() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.boundHandleDocumentClick)
  }

  toggleDropdown() {
    if (this.hasDropdownTarget) {
      const isVisible = !this.dropdownTarget.classList.contains('hidden')
      
      if (isVisible) {
        this.closeDropdown()
      } else {
        this.openDropdown()
      }
    }
  }

  openDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove('hidden')
      this.dropdownTarget.classList.add('opacity-100', 'visible')
      this.dropdownTarget.classList.remove('opacity-0', 'invisible')
      
      // Load fresh notifications when opening
      this.loadRecentNotifications()
    }
  }

  closeDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add('opacity-0', 'invisible')
      this.dropdownTarget.classList.remove('opacity-100', 'visible')
      
      // Hide after transition
      setTimeout(() => {
        this.dropdownTarget.classList.add('hidden')
      }, 200)
    }
  }

  markAsRead(event) {
    const notificationId = event.target.closest('[data-notification-id]')?.dataset.notificationId
    
    if (notificationId && this.subscription) {
      this.subscription.perform('mark_as_read', { 
        notification_id: parseInt(notificationId) 
      })
      
      // Update UI immediately
      this.markNotificationAsReadInUI(notificationId)
    }
  }

  markAllAsRead() {
    if (this.subscription) {
      this.subscription.perform('mark_all_as_read')
      
      // Update UI immediately
      this.updateBadge(0)
      this.markAllNotificationsAsReadInUI()
    }
  }

  loadRecentNotifications() {
    if (this.subscription) {
      this.subscription.perform('get_recent_notifications', { limit: 10 })
    }
  }

  handleNotification(data) {
    switch (data.type) {
      case 'new_notification':
        this.addNewNotification(data.notification)
        this.updateBadge(data.unread_count)
        this.showNotificationToast(data.notification)
        break

      case 'notification_read':
        this.markNotificationAsReadInUI(data.notification_id)
        this.updateBadge(data.unread_count)
        break

      case 'all_notifications_read':
        this.updateBadge(0)
        this.markAllNotificationsAsReadInUI()
        break

      case 'notifications_list':
        this.updateNotificationsList(data.notifications)
        this.updateBadge(data.unread_count)
        break
    }
  }

  addNewNotification(notification) {
    if (this.hasListTarget) {
      const notificationHtml = this.createNotificationHTML(notification)
      this.listTarget.insertAdjacentHTML('afterbegin', notificationHtml)
      
      // Remove excess notifications (keep only 10)
      const notifications = this.listTarget.querySelectorAll('.notification-item')
      if (notifications.length > 10) {
        notifications[notifications.length - 1].remove()
      }
    }
  }

  updateNotificationsList(notifications) {
    if (this.hasListTarget) {
      this.listTarget.innerHTML = notifications.map(notification => 
        this.createNotificationHTML(notification)
      ).join('')
    }
  }

  createNotificationHTML(notification) {
    const readClass = notification.read ? 'bg-gray-50' : 'bg-white border-l-4 border-blue-500'
    const timeAgo = notification.time_ago || 'just now'
    
    return `
      <div class="notification-item ${readClass} p-3 hover:bg-gray-100 cursor-pointer transition-colors"
           data-notification-id="${notification.id}"
           data-action="click->notifications#markAsRead"
           onclick="window.location.href='${notification.url}'">
        <div class="flex items-start space-x-3">
          <div class="flex-shrink-0">
            <i class="${notification.icon} ${notification.color} text-sm"></i>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 truncate">
              ${this.escapeHtml(notification.title)}
            </p>
            ${notification.message ? `
              <p class="text-sm text-gray-600 truncate">
                ${this.escapeHtml(notification.message)}
              </p>
            ` : ''}
            <p class="text-xs text-gray-400 mt-1">
              ${timeAgo}
            </p>
          </div>
          ${!notification.read ? `
            <div class="flex-shrink-0">
              <div class="w-2 h-2 bg-blue-500 rounded-full"></div>
            </div>
          ` : ''}
        </div>
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  updateBadge(count) {
    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = count
      
      if (count > 0) {
        this.badgeTarget.classList.remove('hidden')
        this.badgeTarget.classList.add('inline-flex')
      } else {
        this.badgeTarget.classList.add('hidden')
        this.badgeTarget.classList.remove('inline-flex')
      }
    }
  }

  markNotificationAsReadInUI(notificationId) {
    const notificationElement = this.element.querySelector(`[data-notification-id="${notificationId}"]`)
    if (notificationElement) {
      notificationElement.classList.remove('bg-white', 'border-l-4', 'border-blue-500')
      notificationElement.classList.add('bg-gray-50')
      
      // Remove unread indicator dot
      const unreadDot = notificationElement.querySelector('.w-2.h-2.bg-blue-500')
      if (unreadDot) {
        unreadDot.remove()
      }
    }
  }

  markAllNotificationsAsReadInUI() {
    const notificationElements = this.element.querySelectorAll('.notification-item')
    notificationElements.forEach(element => {
      element.classList.remove('bg-white', 'border-l-4', 'border-blue-500')
      element.classList.add('bg-gray-50')
      
      // Remove unread indicator dots
      const unreadDot = element.querySelector('.w-2.h-2.bg-blue-500')
      if (unreadDot) {
        unreadDot.remove()
      }
    })
  }

  showNotificationToast(notification) {
    // Optional: Show a toast notification for new notifications
    // You can implement this based on your UI preferences
    console.log('New notification:', notification.title)
  }
}